import { useEffect, useRef, useState } from 'react';

export type ScrollDirection = null | 'up' | 'down';

export interface UseScrollPositionProps {
  threshold?: number;
  initialScrollPosition?: number;
}

const useScrollDirection = (props: UseScrollPositionProps) => {
  const {
    threshold = 100,
    initialScrollPosition = window.scrollY,
  } = props;

  const animationFrameId = useRef<number | null>(null);

  const [scrollPosition, setScrollPosition] = useState(initialScrollPosition);
  const [direction, setDirection] = useState<ScrollDirection>(null);

  useEffect(() => {
    function startLoop() {
      if (!animationFrameId.current) {
        animationFrameId.current = window.requestAnimationFrame(loop);
      }
    }

    function loop() {
      const diff = window.scrollY - scrollPosition;

      // Check if we exceed the threshold.
      if (Math.abs(diff) > threshold) {
        // Set the new direction.
        setDirection(Math.sign(diff) === 1 ? 'down' : 'up');

        // Reset the scroll position to the new position.
        setScrollPosition(window.scrollY);
      }

      animationFrameId.current = window.requestAnimationFrame(loop);
    }

    function stopLoop() {
      if (!animationFrameId.current) {
        return;
      }

      window.cancelAnimationFrame(animationFrameId.current);
      animationFrameId.current = null;
    }

    startLoop();

    return () => {
      stopLoop();
    };
  }, [scrollPosition, threshold]);

  return {
    direction,
    scrollPosition,
    setScrollPosition,
  };
};

export default useScrollDirection;
